// Endpoints d'authentification (Option A cookies).

import { apiRequest } from '@/lib/api/client';
import { ApiError, type User } from '@/types/api';

export interface LoginPayload {
  email: string;
  password: string;
}

export interface RegisterPayload {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  password_confirmation: string;
  phone?: string;
  locale?: string;
}

export interface VerifyEmailPayload {
  email: string;
  code: string;
}

export interface ResetPasswordPayload {
  email: string;
  code: string;
  password: string;
  password_confirmation: string;
}

// GET /api/me — renvoie l'utilisateur courant, ou null si non authentifié (401).
export async function fetchMe(): Promise<User | null> {
  try {
    const data = await apiRequest<{ user: User }>('/api/me');
    return data.user;
  } catch (e) {
    if (e instanceof ApiError && e.httpStatus === 401) return null;
    throw e;
  }
}

// POST /api/auth/login — établit la session cookie (côté web) et renvoie le user.
export async function login(payload: LoginPayload): Promise<User> {
  const data = await apiRequest<{ user: User }>('/api/auth/login', { method: 'POST', body: payload });
  return data.user;
}

// POST /api/auth/logout — invalide la session web.
export async function logout(): Promise<void> {
  await apiRequest<null>('/api/auth/logout', { method: 'POST' });
}

// POST /api/auth/register — crée le compte (ne connecte pas ; vérif e-mail requise).
export async function register(payload: RegisterPayload): Promise<User> {
  const data = await apiRequest<{ user: User }>('/api/auth/register', { method: 'POST', body: payload });
  return data.user;
}

// POST /api/auth/verify-email — valide le code 6 chiffres reçu par e-mail.
export async function verifyEmail(payload: VerifyEmailPayload): Promise<void> {
  await apiRequest<unknown>('/api/auth/verify-email', { method: 'POST', body: payload });
}

// POST /api/auth/resend-verification — renvoie un code (réponse uniforme).
export async function resendVerification(email: string): Promise<void> {
  await apiRequest<unknown>('/api/auth/resend-verification', { method: 'POST', body: { email } });
}

// POST /api/auth/forgot-password — envoie un code de réinitialisation (réponse uniforme).
export async function forgotPassword(email: string): Promise<void> {
  await apiRequest<unknown>('/api/auth/forgot-password', { method: 'POST', body: { email } });
}

// POST /api/auth/reset-password — redéfinit le mot de passe via le code. Révoque les tokens.
export async function resetPassword(payload: ResetPasswordPayload): Promise<void> {
  await apiRequest<unknown>('/api/auth/reset-password', { method: 'POST', body: payload });
}
